import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import {
  BarChartIcon,
  BellIcon,
  ChatElipsisIcon,
  CheckmarkCircleIcon,
  ClockIcon,
  ExclamationmarkTriangleIcon,
  PaperplaneIcon,
  TasklistIcon,
} from '@navikt/aksel-icons';
import type { Activity, ActivityType, Channel } from '../../../api/surveyProcess';
import { Routes } from '../../../enums/Routes';
import { buildRoute } from '../../../utils/routeBuilder';
import { activityEntries, channelList, hasMoreActivity } from '../processView';
import classes from './SurveyProcess.module.css';

const ICONS: Record<ActivityType, typeof BellIcon> = {
  SURVEY_SENT: PaperplaneIcon,
  REMINDER_SENT: BellIcon,
  DEADLINE_EXTENDED: ClockIcon,
  TOO_FEW_RESPONSES: ExclamationmarkTriangleIcon,
  RESULTS_READY: CheckmarkCircleIcon,
  RESULT_SHARED: BarChartIcon,
  ACTION_PLAN_INVITATION_SENT: TasklistIcon,
  ACTION_PLAN_SUMMARY_SENT: ChatElipsisIcon,
  ACTION_PLAN_REMINDER_SENT: BellIcon,
};

/** What Trustpulse did in the process, newest first, with the channels each message reached and those it did not */
export const ProcessActivity = ({ activity, teamId }: { activity: Activity[]; teamId: number }) => {
  const { t, i18n } = useTranslation();
  const nameOf = (channel: Channel) => {
    const kind = t(`surveyProcess.channels.${channel.channel}`);
    return channel.name ? t('surveyProcess.channelWithName', { channel: kind, name: channel.name }) : kind;
  };
  const channels = (list: Channel[]) => channelList(list, nameOf, t('surveyProcess.and'));
  const [all, setAll] = useState(false);

  return (
    <section className={classes.activity} aria-labelledby="process-activity">
      <Heading level={3} data-size="2xs" id="process-activity">
        {t('surveyProcess.activity.title')}
      </Heading>
      <ol className={classes.activityList}>
        {activityEntries(activity, i18n.language, all).map((entry) => {
          const Icon = ICONS[entry.type];
          return (
            <li key={entry.id} className={classes.activityEntry}>
              <Icon aria-hidden className={classes.activityIcon} />
              <div className={classes.activityText}>
                <Paragraph data-size="sm">{t(entry.text.key, entry.text.values)}</Paragraph>
                {entry.channels.length > 0 && (
                  <Paragraph data-size="xs" className={classes.subtle}>
                    {t('surveyProcess.activity.sentTo', { channels: channels(entry.channels) })}
                  </Paragraph>
                )}
                {entry.failedChannels.length > 0 && (
                  <Paragraph data-size="xs" className={classes.failed}>
                    {t('surveyProcess.activity.failedTo', { channels: channels(entry.failedChannels) })}{' '}
                    <Link to={buildRoute(Routes.TeamIntegration, { teamId })} className="ds-link">
                      {t('surveyProcess.activity.checkIntegration')}
                    </Link>
                  </Paragraph>
                )}
              </div>
              <time
                className={`ds-paragraph ${classes.subtle} ${classes.activityDate}`}
                data-size="xs"
                dateTime={entry.at}
              >
                {entry.date}
              </time>
            </li>
          );
        })}
      </ol>
      {hasMoreActivity(activity) && (
        <div>
          <Button variant="tertiary" data-size="sm" aria-expanded={all} onClick={() => setAll(!all)}>
            {all
              ? t('surveyProcess.activity.showFewer')
              : t('surveyProcess.activity.showAll', { count: activity.length })}
          </Button>
        </div>
      )}
    </section>
  );
};
